import React, { memo, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../Spinner";
import MetricTrendChart from "../charts/MetricTrendChart";
import RespiratoryResultRow from "./rows/RespiratoryResultRow";
import { useRespiratoryResults } from "../../hooks/results/useRespiratoryResults";
import { getValidatedMeasurementValue } from "../../utils/results/validatedRanges";
import {
  DEFAULT_RESULTS_PAGE_SIZE,
  useInfiniteResultsPageSize,
} from "../../hooks/results/useInfiniteResultsPageSize";

function labelDdMm(ms) {
  const d = new Date(ms);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}`;
}

export default memo(function RespiratoryResults({ db, uid, subjectId }) {
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);
  const resetKey = `${uid || ""}:${subjectId || ""}:respiratory`;
  const { rows, loading, loadingMore, hasMore, error } = useRespiratoryResults(
    db,
    uid,
    {
      pageSize,
      subjectId,
    },
  );
  const { handleScroll } = useInfiniteResultsPageSize({
    canLoadMore: hasMore,
    loading,
    loadingMore,
    rowsLength: rows.length,
    resetKey,
    pageSize,
    setPageSize,
    initialPageSize: DEFAULT_RESULTS_PAGE_SIZE,
  });

  const context = "respiratory"; // deterministic routing

  const onRowClick = useCallback(
    (row) => {
      const variant = "RecordingTidalBreathing";
      const docid = row?.id;
      if (!docid) return;

      const qs =
        `variant=${encodeURIComponent(variant)}` +
        `&docid=${encodeURIComponent(docid)}` +
        `&context=${encodeURIComponent(context)}`;

      navigate(`/result?${qs}`);
    },
    [navigate],
  );

  const trend = useMemo(() => {
    if (!Array.isArray(rows) || !rows.length) {
      return { labels: [], xValues: [], values: [], minMax: [], counts: [] };
    }

    const chron = [...rows].reverse();
    const samples = [];

    for (const r of chron) {
      const ms = r?.timestamp ? Date.parse(r.timestamp) : NaN;
      if (!Number.isFinite(ms)) continue;

      const raw = getValidatedMeasurementValue("rr", r?.rr);
      if (typeof raw !== "number" || !Number.isFinite(raw)) continue;

      samples.push({ ms, v: raw });
    }

    const byDay = new Map(); // dayStartMs -> { sum,n,min,max }
    for (const s of samples) {
      const d = new Date(s.ms);
      const dayStartMs = new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
      ).getTime();

      const cur = byDay.get(dayStartMs) || { sum: 0, n: 0, min: s.v, max: s.v };
      cur.sum += s.v;
      cur.n += 1;
      cur.min = Math.min(cur.min, s.v);
      cur.max = Math.max(cur.max, s.v);
      byDay.set(dayStartMs, cur);
    }

    const days = Array.from(byDay.entries())
      .map(([dayStartMs, agg]) => ({ dayStartMs, ...agg }))
      .sort((a, b) => a.dayStartMs - b.dayStartMs);

    const xValues = days.map((d) => d.dayStartMs);
    const values = days.map((d) => d.sum / d.n);
    const minMax = days.map((d) => ({ min: d.min, max: d.max }));
    const counts = days.map((d) => d.n);
    const labels = xValues.map(labelDdMm);

    return { labels, xValues, values, minMax, counts };
  }, [rows]);

  if (!uid || loading) {
    return (
      <div className="min-h-64 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) return <div className="muted-text">Failed to load results.</div>;
  if (!rows.length) return <div className="muted-text">No results yet.</div>;

  return (
    <div className="w-full h-full min-h-0 flex flex-col gap-2">
      {/* <MetricTrendChart
        height={190}
        labels={trend.labels}
        values={trend.values}
        xValues={trend.xValues}
        xTickFormat="dd/MM"
        lineLabel="RR (Avg)"
        yUnit="br/min"
        yDecimals={0}
        showMinMaxBars={true}
        minMax={trend.minMax}
        counts={trend.counts}
        showTrendLine={true}
      /> */}

      <div
        className="w-full flex-1 min-h-0 overflow-y-auto"
        onScroll={handleScroll}
      >
        {rows.map((r, idx) => (
          <RespiratoryResultRow
            key={r.id ?? idx}
            time={r.time}
            date={r.date}
            rr={r.rr}
            hr={r.hr}
            rrQuality={r.rrQuality}
            hrQuality={r.hrQuality}
            onClick={() => onRowClick(r)}
          />
        ))}
        {loadingMore ? <Spinner size={24} className="py-4" /> : null}
      </div>
    </div>
  );
});
